/**
 * Chat wire-history builder — extracted verbatim from AgentPage.jsx (Phase 2
 * chat-core split). Rebuilds the transcript the backend receives each turn:
 * bounded attachment memory, plus the draft trace the drafting tools use as
 * their revision baseline.
 */
import { htmlToMarkdown } from '../utils/draftUtils';
import { ATTACH_MEMORY_TURNS } from './attachments';

// Rebuild the wire history with each message's attachments. Full image bytes ride
// only on the most recent ATTACH_MEMORY_TURNS attachment turns; older image turns
// degrade to a small text note (the model keeps the context, the payload stays small).
export const buildWireHistory = (msgs) => {
  const attachIdx = [];
  msgs.forEach((m, i) => { if (m.role === 'user' && m.attachments && m.attachments.length) attachIdx.push(i); });
  const live = new Set(attachIdx.slice(-ATTACH_MEMORY_TURNS));
  // The LATEST draft-bearing assistant turn additionally carries the raw HTML —
  // it is the baseline the backend's edit_document tool applies exact
  // find/replace edits to (no regeneration). Older turns stay text-only so the
  // payload doesn't grow with every draft version in the conversation.
  let lastDraftIdx = -1;
  msgs.forEach((m, i) => { if (m.role === 'assistant' && Array.isArray(m.drafts) && m.drafts.length) lastDraftIdx = i; });
  return msgs.map((m, i) => {
    const wire = { role: m.role, content: m.content };
    // Assistant turns that produced a draft carry a compact text trace so the
    // backend can use it as the baseline when the user asks for revisions
    // ("replace X with Y"). MARKDOWN, not flattened plain text — tables and
    // headings must survive the round-trip or formatting instructions are
    // impossible (same reason the draft modals feed htmlToMarkdown back).
    if (m.role === 'assistant' && Array.isArray(m.drafts) && m.drafts.length) {
      const d = m.drafts[m.drafts.length - 1];
      if (d && (d.html || d.plainText)) {
        let text = '';
        if (d.html) { try { text = htmlToMarkdown(d.html); } catch { text = ''; } }
        if (!text) text = d.plainText || '';
        if (text) {
          wire.drafts = [{
            kind: d.kind,
            title: d.title,
            companyCin: d.companyCin || null,
            // T7 — the platform catalog id, when this draft came from a catalog
            // card ("Open in AI Chat" from the draft modals, or a chat draft that
            // already resolved one). It is the identity the backend needs to hold
            // a revision to the card's statutory class instead of re-inferring it
            // from the topic string.
            ...(d.catalogId ? { catalogId: String(d.catalogId) } : {}),
            plainText: String(text).slice(0, 20000),
            ...(i === lastDraftIdx && d.html ? { html: String(d.html).slice(0, 200000) } : {}),
          }];
        }
      }
    }
    if (m.role === 'user' && m.attachments && m.attachments.length) {
      // `id` rides along so the persisted metadata can rematch this device's
      // local image cache on restore (bytes never reach the server).
      wire.attachments = m.attachments.map((a) => {
        const isImage = (a.kind || a.fileType) === 'image';
        if (isImage) {
          if (live.has(i) && a.data) return { id: a.id, name: a.name, kind: 'image', mimeType: a.mimeType, data: a.data };
          return { id: a.id, name: a.name, kind: 'text', text: '[image attached earlier — not re-sent]' };
        }
        // Restored documents have no local text (metadata-only transcript) —
        // degrade to an honest note like images do, never an empty field.
        return {
          id: a.id,
          name: a.name,
          kind: 'text',
          text: a.text || `[file "${a.name}" attached earlier — contents not re-sent]`,
          truncated: a.truncated,
        };
      });
    }
    return wire;
  });
};
